import { z } from 'zod';
/**
 * Problem Details (RFC 7807), wie die API sie bei jedem Fehler liefert —
 * und der eine Sonderfall, den das Web gestaltet statt als Toast zeigt:
 * eine fehlende Betriebskonfiguration.
 *
 * Fehlt `APP_BASE_URL` oder ist SMTP nicht eingerichtet, ist das kein
 * Absturz, sondern ein kontrollierter Zustand. Der Anwender soll lesen, was
 * fehlt, warum die Funktion gerade nicht geht und wer es beheben kann — nie
 * die Systemmeldung.
 */
/** Basis aller `type`-Verweise der API (Produktdomain, nicht die des Büros). */
export const PROBLEM_TYPE_BASE = 'https://docs.ignisai.de/errors/';
export const problemDetails = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
    errors: z.array(z.object({ field: z.string().optional(), message: z.string() })).optional(),
});
// ------------------------------------------------------- Konfiguration fehlt
/**
 * Die Betriebsschlüssel, deren Fehlen eine Funktion sauber abschaltet. Es
 * sind nur die, für die es eine Anwenderfunktion gibt, die ohne sie nicht
 * geht — Tuning-Werte mit Default gehören nicht hierher.
 */
export const konfigurationsSchluessel = z.enum([
    'APP_BASE_URL',
    'SMTP_HOST',
    'OPENAI_API_KEY',
    'MEDIEN_SPEICHER',
]);
export const KONFIGURATIONS_SCHLUESSEL = konfigurationsSchluessel.options;
/** Wie der Schlüssel im UI heißt und wozu er dient — für Anwender wie Administration. */
export const KONFIGURATIONS_SCHLUESSEL_INFO = {
    APP_BASE_URL: {
        bezeichnung: 'Basis-Adresse der Anwendung',
        wozu: 'QR-Codes im Begehungsbericht und auf Etiketten, Links in E-Mails',
    },
    SMTP_HOST: {
        bezeichnung: 'E-Mail-Versand (SMTP)',
        wozu: 'Berichte und Nachweise per E-Mail, Fristen-Erinnerungen',
    },
    OPENAI_API_KEY: {
        bezeichnung: 'KI-Anbieter',
        wozu: 'KI-Entwürfe, Vorschläge und Textprüfung',
    },
    MEDIEN_SPEICHER: {
        bezeichnung: 'Medienablage',
        wozu: 'Fotos, Videos und Unterlagen aus dem Betrieb',
    },
};
export const KONFIGURATION_FEHLT_TYPE = `${PROBLEM_TYPE_BASE}konfiguration-fehlt`;
/**
 * 503 mit Erweiterungsfeld `konfiguration`. `detail` ist für den Anwender
 * formuliert (ganze Sätze, Sie-Form); `hinweisFuerAdmin` zeigt das Web nur
 * Benutzern mit Verwaltungsrecht.
 */
export const konfigurationFehltProblem = problemDetails.extend({
    type: z.literal(KONFIGURATION_FEHLT_TYPE),
    status: z.literal(503),
    konfiguration: z.object({
        schluessel: konfigurationsSchluessel,
        /** Die Funktion, die deshalb nicht geht — als Satzanfang: „Der Begehungsbericht kann nicht erzeugt werden". */
        funktion: z.string(),
        hinweisFuerAdmin: z.string(),
    }),
});
/**
 * Antwort von `GET /admin/konfiguration-status`: je Schlüssel nur
 * „gesetzt/fehlt", nie der Wert — der Weg in den Browser wäre ein Weg heraus.
 */
export const konfigurationsStatus = z.object({
    eintraege: z.array(z.object({
        schluessel: konfigurationsSchluessel,
        gesetzt: z.boolean(),
    })),
});
